import { z } from 'zod';
import {
    CardPointeFrontendConfigEditingOutcome,
    type CardPointeFrontendConfigEditingNormalizedResult,
    type CardPointeFrontendConfigEditingRequestDetails,
    type CardPointeFrontendConfigEditingResponseDetails,
    type CardPointeFrontendConfigEditorValues,
} from '../../../types/ipg/cardpointe/cardpointe-frontend-config-editing.types';
import { CardPointeGatewayId } from '../../../types/ipg/cardpointe/cardpointe-system-constants';

export const CardPointeFrontendConfigEditorValuesSchema: z.ZodObject<{
    enabled: z.ZodBoolean;
    merchantId: z.ZodString;
    gatewayApiBaseUrl: z.ZodString;
    terminalApiBaseUrl: z.ZodString;
    cardSecureTokenizationUrl: z.ZodString;
}> = z.object({
    enabled: z.boolean(),
    merchantId: z.string().min(1),
    gatewayApiBaseUrl: z.string().url(),
    terminalApiBaseUrl: z.string().url(),
    cardSecureTokenizationUrl: z.string().url(),
}) satisfies z.ZodType<CardPointeFrontendConfigEditorValues>;

export const CardPointeFrontendConfigEditingRequestDetailsSchema: z.ZodObject<{
    gatewayId: z.ZodLiteral<typeof CardPointeGatewayId>;
    editorValues: typeof CardPointeFrontendConfigEditorValuesSchema;
}> = z.object({
    gatewayId: z.literal(CardPointeGatewayId),
    editorValues: CardPointeFrontendConfigEditorValuesSchema,
}) satisfies z.ZodType<CardPointeFrontendConfigEditingRequestDetails>;

export const CardPointeFrontendConfigEditingNormalizedResultSchema: z.ZodObject<{
    gatewayId: z.ZodLiteral<typeof CardPointeGatewayId>;
    enabled: z.ZodBoolean;
    merchantId: z.ZodString;
    gatewayApiBaseUrl: z.ZodString;
    terminalApiBaseUrl: z.ZodString;
    cardSecureTokenizationUrl: z.ZodString;
}> = z.object({
    gatewayId: z.literal(CardPointeGatewayId),
    enabled: z.boolean(),
    merchantId: z.string().min(1),
    gatewayApiBaseUrl: z.string().url(),
    terminalApiBaseUrl: z.string().url(),
    cardSecureTokenizationUrl: z.string().url(),
}) satisfies z.ZodType<CardPointeFrontendConfigEditingNormalizedResult>;

export const CardPointeFrontendConfigEditingAppliedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeFrontendConfigEditingOutcome.APPLIED>;
    normalizedResult: typeof CardPointeFrontendConfigEditingNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CardPointeFrontendConfigEditingOutcome.APPLIED),
    normalizedResult: CardPointeFrontendConfigEditingNormalizedResultSchema,
});

export const CardPointeFrontendConfigEditingInvalidConfigurationResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CardPointeFrontendConfigEditingOutcome.INVALID_CONFIGURATION
    >;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(
        CardPointeFrontendConfigEditingOutcome.INVALID_CONFIGURATION
    ),
    message: z.string().min(1),
    body: z.unknown(),
});

export const CardPointeFrontendConfigEditingResponseDetailsSchema: z.ZodType<CardPointeFrontendConfigEditingResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeFrontendConfigEditingAppliedResponseDetailsSchema,
        CardPointeFrontendConfigEditingInvalidConfigurationResponseDetailsSchema,
    ]);
